import i18next from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as yup from 'yup';

export const CP_ADDRESS_TABLE_ALIAS = 'cp_address_book';

export const ADDRESS_DEFAULT_COLUMNS = [
    { id: 'companyName', name: 'Name', visible: true, displayOrder: 1 },
    { id: 'addressType', name: 'Address Type', visible: true, displayOrder: 2 },
    { id: 'fullAddress', name: 'Address', visible: true, displayOrder: 3 },
    { id: 'contactName', name: 'Contact Name', visible: true, displayOrder: 4 },
    { id: 'contactNumber', name: 'Phone Number', visible: true, displayOrder: 5 },
    { id: 'additionalServices', name: 'Additional Services', visible: true, displayOrder: 6 },
];

const t = i18next.t;

export enum AddressType {
    SHIPPER = 'shipper',
    CONSIGNEE = 'consignee',
    BOTH = 'both',
}

export interface IMapAddress {
    id?: string;
    fullAddress: string;
    city: string;
    state: string;
    country: string;
    postal: string;
    latitude: string;
    longitude: string;
    address1: string;
    stateCode: string;
    countryCode?: string;
    streetNumber: string;
}

export interface IAddressForm extends IMapAddress {
    id?: string;
    companyName: string;
    contact: string;
    contactName: string;
    addressType: AddressType;
    address: string;
    pickupAdditionalService: string[];
    deliveryAdditionalService: string[];
    limitedAccessType?: string | null;
    limitedAccessOther?: string | null;
    businessHoursStartTime?: string | null;
    businessHoursEndTime?: string | null;
}

export interface IAddressListRow {
    id: string;
    companyName: string;
    addressType: AddressType;
    contact: string;
    contactNumber: string;
    fullAddress: string;
    contactName: string;
    pickupAdditionalService?: string[];
    deliveryAdditionalService?: string[];
    countryCode?: string;
}

export const addressTypes: any = [
    {
        typeNumber: 1,
        type: 'shipper',
        label: t('AddressBook.Shipper'),
        style: 'blue',
    },
    {
        typeNumber: 2,
        type: 'consignee',
        label: t('Common.Consignee'),
        style: 'warning',
    },
    {
        typeNumber: 3,
        type: 'both',
        label: t('Common.Both'),
        style: 'success',
    },
];

export interface Service {
    id: number;
    name: string;
    type: string;
    slug?: string;
    description?: string;
    isOnlyForFTL?: boolean;
}

export const addAddressSchema = yup.object().shape({
    id: yup.string().optional(),
    addressType: yup.mixed<AddressType>().oneOf(Object.values(AddressType)).required('Address type is required'),
    companyName: yup.string().required(t('AddressBook.validation.Name_Required')),
    contactName: yup.string().required('Contact name is required'),
    contact: yup
        .string()
        .trim()
        .required('Contact is required')
        .test('is-valid-phone-number', 'PhoneNumber must include digits beyond the country code', (value) => {
            if (!value) return true;

            // Normalize to E.164 format — PhoneInput stores code+number without '+' prefix
            const normalizedValue = value.startsWith('+') ? value : `+${value}`;

            if (!/^\+\d{1,4}\d+$/.test(normalizedValue)) return false;

            const phoneNumber = parsePhoneNumberFromString(normalizedValue);

            if (!phoneNumber || !phoneNumber.country) {
                return false;
            }

            return phoneNumber.isValid();
        }),

    address: yup.string().required('Address is required'),
    pickupAdditionalService: yup
        .array()
        .of(yup.string().required())
        .transform((value) => value || [])
        .default([])
        .defined(),
    deliveryAdditionalService: yup
        .array()
        .of(yup.string().required())
        .transform((value) => value || [])
        .default([])
        .defined(),
    fullAddress: yup.string().required('Full address is required'),
    city: yup.string().optional(),
    state: yup.string().optional(),
    country: yup.string().optional(),
    postal: yup.string().optional(),
    latitude: yup.string().optional(),
    longitude: yup.string().optional(),
    limitedAccessType: yup.string().nullable().optional(),
    limitedAccessOther: yup.string().nullable().optional(),
    businessHoursStartTime: yup
        .string()
        .nullable()
        .optional()
        .transform((val) => (val ? val : ''))
        .test('valid-start-time', t('ValidationMessages.Invalid_time_format_HH_MM'), (value) => {
            if (!value) return true;

            return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
        })
        .test('start-required-with-end', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
            const { businessHoursEndTime } = this.parent;

            if (businessHoursEndTime && !value) return false;

            return true;
        }),
    businessHoursEndTime: yup
        .string()
        .nullable()
        .optional()
        .transform((val) => (val ? val : ''))
        .test('valid-end-time', t('ValidationMessages.Invalid_time_format_HH_MM'), (value) => {
            if (!value) return true;

            return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
        })
        .test('end-required-with-start', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
            const { businessHoursStartTime } = this.parent;

            if (businessHoursStartTime && !value) return false;

            return true;
        })
        .test('end-after-start', t('ValidationMessages.Business_Hours_End_After_Start'), function (value) {
            const { businessHoursStartTime } = this.parent;

            if (!businessHoursStartTime || !value) return true;

            return value > businessHoursStartTime;
        }),
});
